import type {Dispatch, SetStateAction} from 'react';
import {useMemo} from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';

import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {useAllSchemasByIdWithSingletonSchemas} from '../useAllSchemasById';
import {buildExecutionEditorConfigurator} from './executionFlowchartConfigurator';
import {FlowchartDataResultType} from './executionFlowchartData';
import type {ExecutionEditorMode, ExecutionFlowchartReadinessSnapshot} from './ExecutionFlowchartPaneTypes';
import type {ExecutionFlowchartRuntimeValue} from './ExecutionFlowchartRuntimeContext';

export enum ExecutionFlowchartPanePhase {
  LOAD_ERROR = 'LOAD_ERROR',
  INACCESSIBLE = 'INACCESSIBLE',
  LOADING = 'LOADING',
  READY = 'READY',
}

export type ExecutionFlowchartPaneReadiness =
  | {phase: ExecutionFlowchartPanePhase.LOAD_ERROR}
  | {phase: ExecutionFlowchartPanePhase.INACCESSIBLE}
  | {phase: ExecutionFlowchartPanePhase.LOADING}
  | {phase: ExecutionFlowchartPanePhase.READY; value: ExecutionFlowchartRuntimeValue};

/** @deprecated Use {@link ExecutionFlowchartPaneReadiness}. */
export type ExecutionFlowchartPaneRuntime = ExecutionFlowchartPaneReadiness;

/**
 * Derives {@link ExecutionFlowchartPaneReadiness} from a draft-root snapshot: editor-mode configurator
 * overrides, optional sidepanel schema queries, and READY/LOADING/ERROR gating. Call only after
 * `useExecutionFlowchartDraftState*` has resolved data and bootstrapped the draft.
 */
export function useExecutionFlowchartPaneReadiness(args: {
  editorMode: ExecutionEditorMode;
  readinessSnapshot: ExecutionFlowchartReadinessSnapshot;
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  onBlockingErrorsChange?: (hasBlockingErrors: boolean) => void;
  onNodeNavigateClick?: (nodeConfigId: string) => void;
  selectedNodeConfigId?: string;
}): ExecutionFlowchartPaneReadiness {
  const {
    editorMode,
    readinessSnapshot,
    draftState,
    setDraftState,
    onBlockingErrorsChange,
    onNodeNavigateClick,
    selectedNodeConfigId,
  } = args;
  const {
    isConfiguratorLoadError,
    dataResult,
    data,
    executionEditorConfigurator: baseExecutionEditorConfigurator,
    externalInaccessible,
    externalPending,
  } = readinessSnapshot;

  const executionEditorConfigurator = useMemo(
    () =>
      baseExecutionEditorConfigurator == null
        ? null
        : buildExecutionEditorConfigurator(baseExecutionEditorConfigurator, editorMode),
    [baseExecutionEditorConfigurator, editorMode]
  );

  const needsSidepanel =
    editorMode !== FlowchartEditorMode.EXECUTION_VIEWER &&
    (executionEditorConfigurator?.hasFeature(ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL) ??
      false);

  const availableTaskSchemas = useMemo(() => data?.availableTaskSchemas, [data]);
  const availableTaskDefinitions = useMemo(() => data?.availableTaskDefinitions, [data]);

  const {
    schemasById: allSchemasById,
    processTaskDefinitionsResult: taskDefinitionsResult,
    singletonTaskSchemasByName,
  } = useAllSchemasByIdWithSingletonSchemas({
    flowchartEditorConfigurator: executionEditorConfigurator,
    rootTaskSchema: data?.rootTaskSchema ?? null,
    skipTaskDefinitionsQuery: !needsSidepanel,
    availableTaskSchemas,
    availableTaskDefinitions: needsSidepanel ? availableTaskDefinitions : undefined,
  });

  return useMemo((): ExecutionFlowchartPaneReadiness => {
    if (externalInaccessible) {
      return {phase: ExecutionFlowchartPanePhase.INACCESSIBLE};
    }
    if (isConfiguratorLoadError || dataResult.resultType === FlowchartDataResultType.ERROR) {
      return {phase: ExecutionFlowchartPanePhase.LOAD_ERROR};
    }
    if (dataResult.resultType === FlowchartDataResultType.INACCESSIBLE) {
      return {phase: ExecutionFlowchartPanePhase.INACCESSIBLE};
    }
    if (externalPending) {
      return {phase: ExecutionFlowchartPanePhase.LOADING};
    }
    if (executionEditorConfigurator == null || data == null || allSchemasById == null || draftState == null) {
      return {phase: ExecutionFlowchartPanePhase.LOADING};
    }

    return {
      phase: ExecutionFlowchartPanePhase.READY,
      value: {
        editorMode,
        data,
        executionEditorConfigurator,
        draftState,
        setDraftState,
        onBlockingErrorsChange,
        onNodeNavigateClick,
        selectedNodeConfigId,
        allSchemasById,
        taskDefinitionsResult,
        singletonTaskSchemasByName,
      },
    };
  }, [
    allSchemasById,
    data,
    dataResult.resultType,
    draftState,
    editorMode,
    executionEditorConfigurator,
    externalInaccessible,
    externalPending,
    isConfiguratorLoadError,
    onBlockingErrorsChange,
    onNodeNavigateClick,
    selectedNodeConfigId,
    setDraftState,
    singletonTaskSchemasByName,
    taskDefinitionsResult,
  ]);
}
